import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  field: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  hint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
}));

const SystemAlertsSettings = () => {
  const classes = useStyles();

  const [enabled, setEnabled] = useState(false);
  const [connectionId, setConnectionId] = useState("");
  const [numbers, setNumbers] = useState("");
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        // Configuração e conexões vêm de endpoints próprios: esta configuração
        // é única para o sistema inteiro, e a lista precisa incluir conexões de
        // TODAS as empresas — a listagem comum é filtrada pela empresa atual.
        const [configRes, connectionsRes] = await Promise.all([
          api.get("/settings/system-alerts"),
          api.get("/settings/system-alerts/connections"),
        ]);
        if (!active) return;
        setEnabled(!!configRes.data?.enabled);
        setConnectionId(
          configRes.data?.connectionId ? String(configRes.data.connectionId) : ""
        );
        setNumbers(configRes.data?.numbers || "");
        setConnections(
          Array.isArray(connectionsRes.data) ? connectionsRes.data : []
        );
      } catch (err) {
        if (active) toast.error(i18n.t("settings.systemAlerts.toasts.loadError"));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  // Mesma regra do backend: só dígitos, mínimo 10, separados por ";".
  const parsedNumbers = numbers
    .split(/[;,\n]/)
    .map((part) => part.replace(/\D/g, ""))
    .filter((digits) => digits.length >= 10);

  const invalidCount =
    numbers.split(/[;,\n]/).filter((p) => p.trim()).length - parsedNumbers.length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/system-alerts", {
        enabled,
        connectionId: connectionId || null,
        numbers,
      });
      setNumbers(data?.numbers || "");
      toast.success(i18n.t("settings.systemAlerts.toasts.saved"));
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        i18n.t("settings.systemAlerts.toasts.saveError");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/system-alerts/test");
      if (data?.sent) {
        toast.success(
          i18n.t("settings.systemAlerts.toasts.testSent", { count: data.destinatarios })
        );
      } else {
        toast.error(
          i18n.t("settings.systemAlerts.toasts.testNotSent", {
            reason: data?.reason || i18n.t("settings.systemAlerts.toasts.unknownError"),
          })
        );
      }
    } catch (err) {
      toast.error(i18n.t("settings.systemAlerts.toasts.testError"));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {i18n.t("settings.systemAlerts.title")}
      </Typography>
      <Typography className={classes.subtitle}>
        {i18n.t("settings.systemAlerts.description")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("settings.systemAlerts.enabled")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl variant="outlined" className={classes.field}>
            <InputLabel id="alert-connection-label">{i18n.t("settings.systemAlerts.connection")}</InputLabel>
            <Select
              labelId="alert-connection-label"
              label={i18n.t("settings.systemAlerts.connection")}
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
            >
              <MenuItem value="">
                <em>{i18n.t("settings.systemAlerts.noConnection")}</em>
              </MenuItem>
              {connections.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.id} — {c.name} · {c.companyName} ({c.status})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography className={classes.hint}>
            {i18n.t("settings.systemAlerts.connectionHint")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            className={classes.field}
            label={i18n.t("settings.systemAlerts.numbers")}
            variant="outlined"
            value={numbers}
            onChange={(e) => setNumbers(e.target.value)}
            placeholder="5544999998888;5544988887777"
            multiline
            minRows={2}
          />
          <Typography className={classes.hint}>
            {i18n.t("settings.systemAlerts.numbersHint")}
            {parsedNumbers.length > 0 &&
              ` ${i18n.t("settings.systemAlerts.validNumbers", { count: parsedNumbers.length })}`}
            {invalidCount > 0 &&
              ` ${i18n.t("settings.systemAlerts.invalidEntries", { count: invalidCount })}`}
          </Typography>
        </Grid>
      </Grid>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : i18n.t("common.save")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleTest}
          disabled={testing || saving}
        >
          {testing ? <CircularProgress size={20} /> : i18n.t("settings.systemAlerts.sendTest")}
        </Button>
      </div>
      <Typography className={classes.hint}>
        {i18n.t("settings.systemAlerts.testHint")}
      </Typography>
    </div>
  );
};

export default SystemAlertsSettings;
